/** Generic inline-CRUD dashboard built from a config. */
import React, { useEffect, useState, useMemo } from "react";
import api, { formatApiError } from "@/api/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Panel, EmptyState, formatEur, formatNum } from "@/components/shared/PageUi";
import { toast } from "sonner";
import { Plus, Search, Trash2, Edit3, X } from "lucide-react";

/**
 * Config shape:
 * {
 *   endpoint: "/hr/employees",
 *   testidPrefix: "employees",
 *   emptyMessage: "Ei työntekijöitä",
 *   defaults: {...},
 *   fields: [
 *      { key:"first_name", label:"Etunimi", type:"text", required:true, span: 1 },
 *      { key:"salary", label:"Palkka", type:"number" },
 *      { key:"status", label:"Tila", type:"select", options:[{value,label}] },
 *      { key:"notes", label:"Muistiinpanot", type:"textarea", span: 2 }
 *   ],
 *   columns: [{ key, label, format?:'eur'|'num'|'date', testid? }],
 *   rowLink?: (item) => "/path",
 *   actions?: (item, onChange) => JSX,  // extra row actions
 *   getRowLabel?: (item) => string,
 * }
 */
export default function CrudList({ config, refreshKey = 0, onItems }) {
  const { endpoint, testidPrefix, emptyMessage = "Ei tietoja", fields, columns, defaults = {}, rowLink, actions, getRowLabel } = config;
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(defaults);
  const [saving, setSaving] = useState(false);
  const [open, setOpen] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get(endpoint, { params: q ? { q } : {} });
      setItems(r.data);
      if (onItems) onItems(r.data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [refreshKey]);

  // debounce search
  useEffect(() => {
    const t = setTimeout(load, 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line
  }, [q]);

  const openNew = () => { setEditing(null); setForm({ ...defaults }); setOpen(true); };
  const openEdit = (it) => { setEditing(it); setForm({ ...defaults, ...it }); setOpen(true); };

  const save = async (e) => {
    e?.preventDefault();
    setSaving(true);
    try {
      // strip read-only fields
      const payload = {};
      fields.forEach((f) => {
        let v = form[f.key];
        if (f.type === "number") v = v === "" || v == null ? 0 : Number(v);
        if (f.type === "boolean") v = Boolean(v);
        payload[f.key] = v;
      });
      let res;
      if (editing?.id) res = await api.put(`${endpoint}/${editing.id}`, payload);
      else res = await api.post(endpoint, payload);
      toast.success(editing ? "Päivitetty" : "Lisätty");
      setOpen(false);
      load();
    } catch (e2) {
      toast.error(formatApiError(e2));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Poistetaanko?")) return;
    try { await api.delete(`${endpoint}/${id}`); toast.success("Poistettu"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const fmt = (it, col) => {
    const v = it[col.key];
    if (v == null || v === "") return "—";
    if (col.format === "eur") return formatEur(v);
    if (col.format === "num") return formatNum(v, col.digits ?? 0);
    if (col.format === "date") return v;
    if (col.format === "badge") {
      return <span className="text-[10px] uppercase tracking-wider px-2 py-1 rounded bg-amber-500/10 text-amber-200 border border-amber-500/30">{v}</span>;
    }
    return String(v);
  };

  return (
    <>
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 mb-4">
        <div className="relative w-full md:max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input placeholder="Hae..." value={q} onChange={(e) => setQ(e.target.value)} data-testid={`${testidPrefix}-search`} className="pl-9 bg-input/40" />
        </div>
        <Button onClick={openNew} data-testid={`${testidPrefix}-new-btn`} className="bg-amber-500 text-zinc-900 hover:bg-amber-400 font-medium">
          <Plus className="w-4 h-4 mr-2" /> Lisää uusi
        </Button>
      </div>

      {loading ? (
        <div className="text-sm text-muted-foreground py-8 text-center">Ladataan...</div>
      ) : items.length === 0 ? (
        <EmptyState
          icon={<Plus className="w-12 h-12" />}
          message={emptyMessage}
          action={<Button onClick={openNew} className="bg-amber-500 text-zinc-900 hover:bg-amber-400">Lisää ensimmäinen</Button>}
        />
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs uppercase tracking-wider text-muted-foreground border-b border-amber-500/20">
                {columns.map((c) => (
                  <th key={c.key} className={`py-3 px-2 ${c.align === "right" ? "text-right" : ""}`}>{c.label}</th>
                ))}
                <th className="py-3 px-2 w-20"></th>
              </tr>
            </thead>
            <tbody>
              {items.map((it) => (
                <tr key={it.id} data-testid={`${testidPrefix}-row-${it.id}`} onClick={() => openEdit(it)}
                    className="border-b border-amber-500/10 hover:bg-amber-500/5 cursor-pointer transition">
                  {columns.map((c) => (
                    <td key={c.key} className={`py-3 px-2 ${c.align === "right" ? "text-right" : ""}`}>{fmt(it, c)}</td>
                  ))}
                  <td className="py-3 px-2">
                    <div className="flex gap-1 justify-end">
                      {actions && actions(it, load)}
                      <Button size="icon" variant="ghost" onClick={(e) => { e.stopPropagation(); openEdit(it); }}>
                        <Edit3 className="w-4 h-4" />
                      </Button>
                      <Button size="icon" variant="ghost" onClick={(e) => { e.stopPropagation(); remove(it.id); }} data-testid={`${testidPrefix}-delete-${it.id}`}>
                        <Trash2 className="w-4 h-4 text-destructive" />
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent side="right" className="bg-background border-l border-amber-500/30 w-full sm:max-w-xl overflow-y-auto" data-testid={`${testidPrefix}-form-sheet`}>
          <SheetHeader>
            <SheetTitle className="font-display gold-text">
              {editing ? `Muokkaa: ${getRowLabel ? getRowLabel(editing) : ""}` : "Lisää uusi"}
            </SheetTitle>
          </SheetHeader>
          <form onSubmit={save} className="grid grid-cols-2 gap-3 mt-6">
            {fields.map((f) => (
              <FieldRender key={f.key} field={f} value={form[f.key]} onChange={(v) => setForm({ ...form, [f.key]: v })} testidPrefix={testidPrefix} />
            ))}
            <div className="col-span-2 flex justify-end gap-2 pt-4 border-t border-amber-500/20 mt-2">
              <Button type="button" variant="outline" onClick={() => setOpen(false)} className="border-amber-500/40 text-amber-200 hover:bg-amber-500/10">
                Peruuta
              </Button>
              <Button type="submit" disabled={saving} data-testid={`${testidPrefix}-save-btn`} className="bg-amber-500 text-zinc-900 hover:bg-amber-400 font-medium">
                {saving ? "Tallennetaan..." : (editing ? "Tallenna muutokset" : "Tallenna")}
              </Button>
            </div>
          </form>
        </SheetContent>
      </Sheet>
    </>
  );
}

function FieldRender({ field, value, onChange, testidPrefix }) {
  const span = field.span || 1;
  const cls = span === 2 ? "col-span-2" : "col-span-2 md:col-span-1";
  const testid = `${testidPrefix}-field-${field.key}`;

  if (field.type === "textarea") {
    return (
      <div className={`${cls} space-y-2`}>
        <Label>{field.label}</Label>
        <Textarea value={value || ""} onChange={(e) => onChange(e.target.value)} rows={field.rows || 3} data-testid={testid} className="bg-input/40" />
      </div>
    );
  }
  if (field.type === "select") {
    return (
      <div className={`${cls} space-y-2`}>
        <Label>{field.label}</Label>
        <Select value={value || ""} onValueChange={onChange}>
          <SelectTrigger data-testid={testid} className="bg-input/40"><SelectValue placeholder={field.placeholder || "Valitse"} /></SelectTrigger>
          <SelectContent>
            {(field.options || []).map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
    );
  }
  if (field.type === "boolean") {
    return (
      <div className={`${cls} flex items-center justify-between gap-2 py-2`}>
        <Label>{field.label}</Label>
        <Switch checked={Boolean(value)} onCheckedChange={onChange} data-testid={testid} />
      </div>
    );
  }
  return (
    <div className={`${cls} space-y-2`}>
      <Label>{field.label} {field.required ? "*" : ""}</Label>
      <Input
        type={field.type === "number" ? "number" : field.type === "date" ? "date" : "text"}
        step={field.type === "number" ? (field.step ?? "0.01") : undefined}
        value={value ?? ""}
        onChange={(e) => onChange(e.target.value)}
        required={field.required}
        placeholder={field.placeholder}
        data-testid={testid}
        className="bg-input/40"
      />
    </div>
  );
}
